import type { Automation } from '../../api';

/**
 * Settings → Automations' words: a row's second line, and when a run is,
 * read on the automation's own clock. Pure, with `now` passed in, as
 * format.ts is.
 */

/** A day in `timeZone` as a number that sorts: 20271231. */
function dayIn(seconds: number, timeZone: string): number {
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(seconds * 1000))) {
    p[part.type] = part.value;
  }
  return Number(`${p.year}${p.month}${p.day}`);
}

const clock = (seconds: number, timeZone: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(seconds * 1000));

/** The day a run is on: "today", "tomorrow", "Tue" within the week, "12 Oct"
 *  after. */
export function runDay(at: number, timeZone: string, now: number = Date.now() / 1000): string {
  const days = Math.round((Date.UTC(...ymd(dayIn(at, timeZone))) - Date.UTC(...ymd(dayIn(now, timeZone)))) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  const date = new Date(at * 1000);
  if (days > 1 && days < 7) return date.toLocaleDateString('en-GB', { timeZone, weekday: 'short' });
  return date.toLocaleDateString('en-GB', { timeZone, day: 'numeric', month: 'short' });
}

const ymd = (day: number): [number, number, number] => [Math.floor(day / 10_000), (Math.floor(day / 100) % 100) - 1, day % 100];

/** A row's second line: when it runs, then its next run or that it is off,
 *  then a last run that did not go. */
export function automationLine(a: Automation, now: number = Date.now() / 1000): string {
  const next = a.enabled && a.nextRunAt ? `Next run ${runDay(a.nextRunAt, a.timeZone, now)} ${clock(a.nextRunAt, a.timeZone)}` : 'Off';
  const parts = [a.when, next];
  if (a.lastRun?.outcome === 'skipped') parts.push('Last run skipped');
  if (a.lastRun?.outcome === 'failed') parts.push('Last run did not start');
  return parts.join(' · ');
}
